/* eslint-disable no-param-reassign */
import { deleteProductType } from '../../productTypes/delete';
import { renameProductType } from '../../productTypes/rename';
import { deleteProductVendor } from '../../productVendors/delete';
import { renameProductVendor } from '../../productVendors/rename';

const normalizeProductTypeTitle = (title) => {
  if (!title) return '';
  return title.trim().replace(/\s+/g, ' ').toLowerCase();
};

const normalizeVendorTitle = (title) => {
  if (!title) return '';
  return title.trim().replace(/\s+/g, ' ').toLowerCase();
};

/** mirrors `products.renameProductTypeOnMasters` / `buildNormalizedTitleMatchRegex` case+whitespace rules */
export const productTypeTitleMatches = (storedTitle, oldTitle) => (
  normalizeProductTypeTitle(storedTitle) === normalizeProductTypeTitle(oldTitle)
);

/** mirrors `products.renameVendorOnMasters` / `buildNormalizedTitleMatchRegex` case+whitespace rules */
export const vendorTitleMatches = (storedTitle, oldTitle) => (
  normalizeVendorTitle(storedTitle) === normalizeVendorTitle(oldTitle)
);

/** Propagate a registry rename onto listing draft `productType` fields in entities (not live snapshots). */
const renameProductTypeOnListingDrafts = (state, originalTitle, newTitle) => {
  const listings = state.entities?.listings;
  if (!listings || !originalTitle || !newTitle) return;

  Object.values(listings).forEach((listing) => {
    if (listing && productTypeTitleMatches(listing.productType, originalTitle)) {
      listing.productType = newTitle;
    }
  });
};

/** Clear listing draft `productType` fields matching a deleted registry title (not live snapshots). */
const clearProductTypeOnListingDrafts = (state, title) => {
  const listings = state.entities?.listings;
  if (!listings || !title) return;

  Object.values(listings).forEach((listing) => {
    if (listing && productTypeTitleMatches(listing.productType, title)) {
      listing.productType = '';
    }
  });
};

/** Propagate a registry rename onto listing draft `vendor` fields in entities (not live snapshots). */
const renameVendorOnListingDrafts = (state, originalTitle, newTitle) => {
  const listings = state.entities?.listings;
  if (!listings || !originalTitle || !newTitle) return;

  Object.values(listings).forEach((listing) => {
    if (listing && vendorTitleMatches(listing.vendor, originalTitle)) {
      listing.vendor = newTitle;
    }
  });
};

/** Clear listing draft `vendor` fields matching a deleted registry title (not live snapshots). */
const clearVendorOnListingDrafts = (state, title) => {
  const listings = state.entities?.listings;
  if (!listings || !title) return;

  Object.values(listings).forEach((listing) => {
    if (listing && vendorTitleMatches(listing.vendor, title)) {
      listing.vendor = '';
    }
  });
};

export const reducer = (builder) => {
  builder
    .addCase(renameProductType.fulfilled, (state, { payload, meta }) => {
      if (!payload || payload.cancelled) return;
      const { originalTitle } = meta.arg;
      const newTitle = payload.productType.title;
      if (!originalTitle || !newTitle || originalTitle === newTitle) return;

      state.items.forEach((product) => {
        if (productTypeTitleMatches(product.productType, originalTitle)) {
          product.productType = newTitle;
        }
      });
      renameProductTypeOnListingDrafts(state, originalTitle, newTitle);
    })
    .addCase(renameProductVendor.fulfilled, (state, { payload, meta }) => {
      if (!payload || payload.cancelled) return;
      const { originalTitle } = meta.arg;
      const newTitle = payload.productVendor.title;
      if (!originalTitle || !newTitle || originalTitle === newTitle) return;

      state.items.forEach((product) => {
        if (vendorTitleMatches(product.vendor, originalTitle)) {
          product.vendor = newTitle;
        }
      });
      renameVendorOnListingDrafts(state, originalTitle, newTitle);
    })
    .addCase(deleteProductType.fulfilled, (state, { payload, meta }) => {
      if (!payload || payload.cancelled) return;
      const { title } = meta.arg;
      if (!title) return;

      state.items.forEach((product) => {
        if (productTypeTitleMatches(product.productType, title)) {
          product.productType = '';
        }
      });
      clearProductTypeOnListingDrafts(state, title);
    })
    .addCase(deleteProductVendor.fulfilled, (state, { payload, meta }) => {
      if (!payload || payload.cancelled) return;
      const { title } = meta.arg;
      if (!title) return;

      state.items.forEach((product) => {
        if (vendorTitleMatches(product.vendor, title)) {
          product.vendor = '';
        }
      });
      clearVendorOnListingDrafts(state, title);
    });
};
